/**
 * @fileoverview Prescription & Dispensation Overview Page
 * Shows comparison between prescriptions and dispensations with prescriber/dispenser breakdown.
 * - Doctors see only their own data
 * - Other users see all data
 */

import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import {
    Grid, Box, Typography, Skeleton, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Tooltip, Popover,
    FormControl, InputLabel, Select, MenuItem, Button, Chip, Paper
} from "@mui/material";
import { intl, DmsPage, DmsAltBox, fetchData, HTTP_STATUS, LanguageBaseContext } from "dmsbasereact";

import DescriptionIcon from "@mui/icons-material/Description";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import PeopleIcon from "@mui/icons-material/People";

import { getMonthOptions } from "../../constants/MonthOptions";

const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
        years.push(y);
    }
    return years;
};

const card3DStyle = {
    borderRadius: 2,
    position: "relative",
    background: "background.paper",
    borderTop: "2px solid rgba(0, 0, 0, 0.08)",
    borderLeft: "2px solid rgba(0, 0, 0, 0.06)",
    borderBottom: "3px solid rgba(0, 0, 0, 0.2)",
    borderRight: "3px solid rgba(0, 0, 0, 0.15)",
    boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.12), inset -1px -1px 2px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
        transform: "translateY(-2px) translateX(-1px)",
        boxShadow: "6px 6px 12px rgba(0, 0, 0, 0.18), inset -2px -2px 3px rgba(0, 0, 0, 0.08)",
    },
};

const StatCard = ({ title, value, icon: Icon, color, locale }) => (
    <Paper sx={{ p: 2, height: "100%", minHeight: 100, ...card3DStyle }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: `${color}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}>
                <Icon sx={{ fontSize: 32, color }} />
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight="bold" color={color}>
                    {typeof value === "number" ? value.toLocaleString(locale) : value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {title}
                </Typography>
            </Box>
        </Box>
    </Paper>
);

const MonthlyBarGroup = ({ label, prescriptions, dispensations, maxValue, locale }) => {
    const prescHeight = maxValue > 0 ? (prescriptions / maxValue) * 100 : 0;
    const dispHeight = maxValue > 0 ? (dispensations / maxValue) * 100 : 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 60, flex: 1 }}>
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end", height: 180 }}>
                <Tooltip title={`${intl.get("Reports.PrescDisp.Prescriptions").d("Prescrições")}: ${prescriptions.toLocaleString(locale)}`}>
                    <Box sx={{
                        width: 20,
                        height: `${prescHeight}%`,
                        backgroundColor: "#1976d2",
                        borderRadius: "4px 4px 0 0",
                        minHeight: prescriptions > 0 ? 4 : 0,
                        transition: "height 0.3s ease-in-out",
                        cursor: "pointer",
                        "&:hover": { opacity: 0.8 }
                    }} />
                </Tooltip>
                <Tooltip title={`${intl.get("Reports.PrescDisp.Dispensations").d("Dispensações")}: ${dispensations.toLocaleString(locale)}`}>
                    <Box sx={{
                        width: 20,
                        height: `${dispHeight}%`,
                        backgroundColor: "#388e3c",
                        borderRadius: "4px 4px 0 0",
                        minHeight: dispensations > 0 ? 4 : 0,
                        transition: "height 0.3s ease-in-out",
                        cursor: "pointer",
                        "&:hover": { opacity: 0.8 }
                    }} />
                </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: "center", fontSize: "0.7rem" }}>
                {label}
            </Typography>
        </Box>
    );
};

const MonthlyChart = ({ data, loading, locale }) => {
    if (loading) {
        return <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />;
    }

    if (!data?.length) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 280 }}>
                <Typography color="text.secondary">
                    {intl.get("DmsAnalytics.NoData").d("Sem dados disponíveis")}
                </Typography>
            </Box>
        );
    }

    const maxValue = Math.max(...data.map(m => Math.max(m.prescriptions, m.dispensations)), 1);

    return (
        <Box>
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: 1, backgroundColor: "#1976d2" }} />
                    <Typography variant="body2">
                        {intl.get("Reports.PrescDisp.Prescriptions").d("Prescrições")}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: 1, backgroundColor: "#388e3c" }} />
                    <Typography variant="body2">
                        {intl.get("Reports.PrescDisp.Dispensations").d("Dispensações")}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 1,
                justifyContent: data.length <= 6 ? "space-around" : "flex-start"
            }}>
                {data.map((item) => (
                    <MonthlyBarGroup
                        key={item.month}
                        label={item.month_label}
                        prescriptions={item.prescriptions}
                        dispensations={item.dispensations}
                        maxValue={maxValue}
                        locale={locale}
                    />
                ))}
            </Box>
        </Box>
    );
};

const PrescribersTable = ({ data, loading, onPrescriberClick, selectedPrescriber }) => {
    if (loading) {
        return <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 1 }} />;
    }

    if (!data?.items?.length) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
                <Typography color="text.secondary">
                    {intl.get("DmsAnalytics.NoData").d("Sem dados disponíveis")}
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                            {intl.get("Reports.PrescDisp.Prescriber").d("Prescritor")}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap", minWidth: 60 }}>
                            {intl.get("Reports.PrescDisp.Presc").d("Presc")}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap", minWidth: 60 }}>
                            {intl.get("Reports.PrescDisp.Disp").d("Disp")}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((item, index) => (
                        <TableRow
                            key={item.doctor_pk || index}
                            hover
                            selected={selectedPrescriber === item.doctor_pk}
                            sx={{ cursor: "pointer" }}
                            onClick={() => onPrescriberClick?.(item)}
                        >
                            <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <PersonIcon sx={{ fontSize: 16, color: "primary.main" }} />
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{
                                                maxWidth: 200,
                                                color: selectedPrescriber === item.doctor_pk ? "primary.main" : "inherit",
                                                fontWeight: selectedPrescriber === item.doctor_pk ? "bold" : "normal"
                                            }}
                                        >
                                            {item.doctor_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            CRM: {item.crm}
                                        </Typography>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell align="right">
                                <Typography color="primary.main" fontWeight="medium">
                                    {item.prescription_count}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography color="success.main" fontWeight="medium">
                                    {item.dispensation_count}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const DispensersTable = ({ data, loading, isDoctorView, onDispenserClick, selectedDispenser }) => {
    if (loading) {
        return <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 1 }} />;
    }

    if (!data?.items?.length) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
                <Typography color="text.secondary">
                    {intl.get("DmsAnalytics.NoData").d("Sem dados disponíveis")}
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                            {intl.get("Reports.PrescDisp.Dispenser").d("Dispensador")}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap", minWidth: 60 }}>
                            {intl.get("Reports.PrescDisp.Disp").d("Disp")}
                        </TableCell>
                        {!isDoctorView && (
                            <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap", minWidth: 60 }}>
                                {intl.get("Reports.PrescDisp.Presc").d("Presc")}
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((item, index) => (
                        <TableRow
                            key={item.pharmacist_pk || index}
                            hover
                            selected={selectedDispenser === item.pharmacist_pk}
                            sx={{ cursor: "pointer" }}
                            onClick={() => onDispenserClick?.(item)}
                        >
                            <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <LocalPharmacyIcon sx={{ fontSize: 16, color: "success.main" }} />
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{
                                                maxWidth: 200,
                                                color: selectedDispenser === item.pharmacist_pk ? "success.main" : "inherit",
                                                fontWeight: selectedDispenser === item.pharmacist_pk ? "bold" : "normal"
                                            }}
                                        >
                                            {item.pharmacist_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            CRF: {item.crf}
                                        </Typography>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell align="right">
                                <Typography color="success.main" fontWeight="medium">
                                    {item.dispensation_count}
                                </Typography>
                            </TableCell>
                            {!isDoctorView && (
                                <TableCell align="right">
                                    <Typography color="text.secondary">
                                        {item.prescribers_count}
                                    </Typography>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export const PrescriptionDispensationOverviewPage = () => {
    const { language } = useContext(LanguageBaseContext);

    // Calculate current date values
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    // Calculate default start date (11 months ago)
    const getDefaultStartDate = useCallback(() => {
        const d = new Date(currentYear, currentMonth - 1 - 11, 1);
        return { month: d.getMonth() + 1, year: d.getFullYear() };
    }, [currentMonth, currentYear]);

    const defaultStartDate = getDefaultStartDate();

    // Initialize state with lazy initialization to ensure correct values
    const [startMonth, setStartMonth] = useState(() => {
        const d = new Date(new Date().getFullYear(), new Date().getMonth() - 11, 1);
        return d.getMonth() + 1;
    });
    const [startYear, setStartYear] = useState(() => {
        const d = new Date(new Date().getFullYear(), new Date().getMonth() - 11, 1);
        return d.getFullYear();
    });
    const [endMonth, setEndMonth] = useState(() => new Date().getMonth() + 1);
    const [endYear, setEndYear] = useState(() => new Date().getFullYear());

    const [filterAnchor, setFilterAnchor] = useState(null);
    const [summary, setSummary] = useState(null);
    const [monthlyData, setMonthlyData] = useState([]);
    const [monthlyTotals, setMonthlyTotals] = useState(null);
    const [prescribers, setPrescribers] = useState(null);
    const [dispensers, setDispensers] = useState(null);
    const [loading, setLoading] = useState(true);

    // Filter by prescriber/dispenser
    const [selectedPrescriber, setSelectedPrescriber] = useState(null);
    const [selectedPrescriberName, setSelectedPrescriberName] = useState("");
    const [selectedDispenser, setSelectedDispenser] = useState(null);
    const [selectedDispenserName, setSelectedDispenserName] = useState("");

    // Track if this is the first render to avoid double-loading
    const isFirstRender = useRef(true);

    const yearOptions = useMemo(() => getYearOptions(), []);
    // language dependency triggers recalculation when language changes (intl translations)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const monthOptions = useMemo(() => getMonthOptions(), [language]);

    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();
        params.append("years", `${startYear},${endYear}`);
        params.append("start_month", startMonth);
        params.append("end_month", endMonth);
        if (selectedPrescriber) {
            params.append("doctor_pk", selectedPrescriber);
        }
        if (selectedDispenser) {
            params.append("pharmacist_pk", selectedDispenser);
        }
        return params.toString();
    }, [startMonth, startYear, endMonth, endYear, selectedPrescriber, selectedDispenser]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const queryParams = buildQueryParams();
            const [summaryRes, monthlyRes, prescribersRes, dispensersRes] = await Promise.all([
                fetchData(`/reports/prescription-dispensation/summary?${queryParams}`, "GET", true),
                fetchData(`/reports/prescription-dispensation/monthly?${queryParams}`, "GET", true),
                fetchData(`/reports/prescription-dispensation/prescribers?${queryParams}`, "GET", true),
                fetchData(`/reports/prescription-dispensation/dispensers?${queryParams}`, "GET", true),
            ]);

            if (summaryRes.status === HTTP_STATUS.OK_200) {
                setSummary(summaryRes.data?.data);
            } else {
                console.error("Summary endpoint error:", summaryRes.status, summaryRes.data);
            }
            if (monthlyRes.status === HTTP_STATUS.OK_200) {
                const monthlyResponseData = monthlyRes.data?.data;
                setMonthlyData(monthlyResponseData?.items || []);
                setMonthlyTotals({
                    total_prescriptions: monthlyResponseData?.total_prescriptions || 0,
                    total_dispensations: monthlyResponseData?.total_dispensations || 0,
                });
            }
            if (prescribersRes.status === HTTP_STATUS.OK_200) {
                setPrescribers(prescribersRes.data?.data);
            }
            if (dispensersRes.status === HTTP_STATUS.OK_200) {
                setDispensers(dispensersRes.data?.data);
            }
        } catch (err) {
            console.error("Error loading prescription-dispensation data:", err);
        } finally {
            setLoading(false);
        }
    }, [buildQueryParams]);

    // Load data on mount with default filter (last 12 months)
    useEffect(() => {
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFilterClick = (event) => {
        setFilterAnchor(event.currentTarget);
    };

    const handleFilterClose = () => {
        setFilterAnchor(null);
    };

    const handleApplyFilter = () => {
        handleFilterClose();
        loadData();
    };

    const handleResetFilter = () => {
        setStartMonth(defaultStartDate.month);
        setStartYear(defaultStartDate.year);
        setEndMonth(currentMonth);
        setEndYear(currentYear);
        setSelectedPrescriber(null);
        setSelectedPrescriberName("");
        setSelectedDispenser(null);
        setSelectedDispenserName("");
        handleFilterClose();
    };

    const handlePrescriberClick = (item) => {
        if (selectedPrescriber === item.doctor_pk) {
            // Deselect if clicking the same one
            setSelectedPrescriber(null);
            setSelectedPrescriberName("");
        } else {
            setSelectedPrescriber(item.doctor_pk);
            setSelectedPrescriberName(item.doctor_name);
        }
    };

    const handleDispenserClick = (item) => {
        if (selectedDispenser === item.pharmacist_pk) {
            // Deselect if clicking the same one
            setSelectedDispenser(null);
            setSelectedDispenserName("");
        } else {
            setSelectedDispenser(item.pharmacist_pk);
            setSelectedDispenserName(item.pharmacist_name);
        }
    };

    const handleClearPrescriberFilter = useCallback(() => {
        setSelectedPrescriber(null);
        setSelectedPrescriberName("");
    }, []);

    const handleClearDispenserFilter = useCallback(() => {
        setSelectedDispenser(null);
        setSelectedDispenserName("");
    }, []);

    const isFilterActive = startMonth !== defaultStartDate.month ||
        startYear !== defaultStartDate.year ||
        endMonth !== currentMonth || endYear !== currentYear ||
        selectedPrescriber || selectedDispenser;

    // Reload data when prescriber/dispenser filter changes (including when cleared)
    useEffect(() => {
        // Skip the first render (initial mount) to avoid double-loading
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        loadData();
    }, [selectedPrescriber, selectedDispenser]); // eslint-disable-line react-hooks/exhaustive-deps

    const getFilterLabel = () => {
        const startLabel = `${monthOptions.find(m => m.value === startMonth)?.label?.slice(0, 3)}/${startYear}`;
        const endLabel = `${monthOptions.find(m => m.value === endMonth)?.label?.slice(0, 3)}/${endYear}`;
        if (startMonth === endMonth && startYear === endYear) {
            return startLabel;
        }
        return `${startLabel} - ${endLabel}`;
    };

    const isDoctorView = prescribers?.is_doctor_view || false;

    return (
        <DmsPage
            key={`prescription-dispensation-${language}`}
            name={intl.get("Reports.PrescDisp.PageTitle").d("Prescrições & Dispensações")}
            manual_slug="reports-prescription-dispensation"
        >
            {/* Header with Filter */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Typography variant="h5" fontWeight="bold">
                    {intl.get("Reports.PrescDisp.GeneralData").d("Dados Gerais")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                        label={getFilterLabel()}
                        size="small"
                        color={isFilterActive ? "primary" : "default"}
                        variant={isFilterActive ? "filled" : "outlined"}
                    />
                    {selectedPrescriber && (
                        <Chip
                            icon={<PersonIcon sx={{ fontSize: 16 }} />}
                            label={selectedPrescriberName}
                            size="small"
                            color="primary"
                            onDelete={handleClearPrescriberFilter}
                        />
                    )}
                    {selectedDispenser && (
                        <Chip
                            icon={<LocalPharmacyIcon sx={{ fontSize: 16 }} />}
                            label={selectedDispenserName}
                            size="small"
                            color="success"
                            onDelete={handleClearDispenserFilter}
                        />
                    )}
                    {isFilterActive && (
                        <Tooltip title={intl.get("ClearFilters").d("Clear Filters")}>
                            <IconButton onClick={handleResetFilter} size="small">
                                <FilterAltOffIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title={intl.get("Filter").d("Filtrar")}>
                        <IconButton onClick={handleFilterClick} size="small">
                            <FilterListIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={intl.get("Refresh").d("Refresh")}>
                        <IconButton onClick={loadData} size="small" disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
                    ) : (
                        <StatCard
                            title={intl.get("Reports.PrescDisp.TotalPrescriptions").d("Total Prescrições")}
                            value={summary?.total_prescriptions || monthlyTotals?.total_prescriptions || 0}
                            icon={DescriptionIcon}
                            color="#1976d2"
                            locale={language}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={4}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
                    ) : (
                        <StatCard
                            title={intl.get("Reports.PrescDisp.TotalDispensations").d("Total Dispensações")}
                            value={summary?.total_dispensations || monthlyTotals?.total_dispensations || 0}
                            icon={LocalShippingIcon}
                            color="#388e3c"
                            locale={language}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={4}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2 }} />
                    ) : (
                        <StatCard
                            title={intl.get("Reports.PrescDisp.PatientsWithPrescriptions").d("Patients")}
                            value={summary?.total_patients_with_prescriptions || 0}
                            icon={PeopleIcon}
                            color="#7b1fa2"
                            locale={language}
                        />
                    )}
                </Grid>
            </Grid>

            {/* Monthly Evolution Chart */}
            <DmsAltBox.Card sx={{ mb: 3, ...card3DStyle }}>
                <DmsAltBox.Header>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "common.white" }}>
                        {intl.get("Reports.PrescDisp.MonthlyEvolution").d("Monthly Evolution")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <MonthlyChart data={monthlyData} loading={loading} locale={language} />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            {/* Prescribers and Dispensers Side by Side */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <DmsAltBox.Card sx={{ height: "100%", ...card3DStyle }}>
                        <DmsAltBox.Header>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <PersonIcon />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "common.white" }}>
                                    {isDoctorView
                                        ? intl.get("Reports.PrescDisp.MyPrescriptions").d("Minhas Prescrições")
                                        : intl.get("Reports.PrescDisp.Prescribers").d("Prescritores")
                                    }
                                </Typography>
                            </Box>
                        </DmsAltBox.Header>
                        <DmsAltBox.Body>
                            <PrescribersTable
                                data={prescribers}
                                loading={loading}
                                onPrescriberClick={handlePrescriberClick}
                                selectedPrescriber={selectedPrescriber}
                            />
                        </DmsAltBox.Body>
                    </DmsAltBox.Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <DmsAltBox.Card sx={{ height: "100%", ...card3DStyle }}>
                        <DmsAltBox.Header>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <LocalPharmacyIcon />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "common.white" }}>
                                    {isDoctorView
                                        ? intl.get("Reports.PrescDisp.WhoDispensedMyPrescriptions").d("Quem Dispensou")
                                        : intl.get("Reports.PrescDisp.Dispensers").d("Dispensadores")
                                    }
                                </Typography>
                            </Box>
                        </DmsAltBox.Header>
                        <DmsAltBox.Body>
                            <DispensersTable
                                data={dispensers}
                                loading={loading}
                                isDoctorView={isDoctorView}
                                onDispenserClick={handleDispenserClick}
                                selectedDispenser={selectedDispenser}
                            />
                        </DmsAltBox.Body>
                    </DmsAltBox.Card>
                </Grid>
            </Grid>

            {/* Filter Popover */}
            <Popover
                open={Boolean(filterAnchor)}
                anchorEl={filterAnchor}
                onClose={handleFilterClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Box sx={{ p: 3, minWidth: 320 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        {intl.get("Reports.PrescDisp.FilterPeriod").d("Filtrar Período")}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {intl.get("Reports.PrescDisp.StartPeriod").d("Início")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>{intl.get("Month").d("Mês")}</InputLabel>
                            <Select
                                value={startMonth}
                                label={intl.get("Month").d("Mês")}
                                onChange={(e) => setStartMonth(e.target.value)}
                            >
                                {monthOptions.map((m) => (
                                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>{intl.get("Year").d("Ano")}</InputLabel>
                            <Select
                                value={startYear}
                                label={intl.get("Year").d("Ano")}
                                onChange={(e) => setStartYear(e.target.value)}
                            >
                                {yearOptions.map((y) => (
                                    <MenuItem key={y} value={y}>{y}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {intl.get("Reports.PrescDisp.EndPeriod").d("Fim")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>{intl.get("Month").d("Mês")}</InputLabel>
                            <Select
                                value={endMonth}
                                label={intl.get("Month").d("Mês")}
                                onChange={(e) => setEndMonth(e.target.value)}
                            >
                                {monthOptions.map((m) => (
                                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>{intl.get("Year").d("Ano")}</InputLabel>
                            <Select
                                value={endYear}
                                label={intl.get("Year").d("Ano")}
                                onChange={(e) => setEndYear(e.target.value)}
                            >
                                {yearOptions.map((y) => (
                                    <MenuItem key={y} value={y}>{y}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                        <Button variant="contained" size="small" onClick={handleApplyFilter}>
                            {intl.get("Apply").d("Apply")}
                        </Button>
                        <Button variant="outlined" size="small" onClick={handleResetFilter}>
                            {intl.get("Reset").d("Reset")}
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </DmsPage>
    );
};

export default PrescriptionDispensationOverviewPage;
